import React, { useState, useRef, useEffect } from 'react';
import '../styles/components/spin-wheel.css';

interface Prize {
  id: number;
  text: string;
  color: string;
  textColor?: string;
}

interface SpinWheelProps {
  onPrizeWon?: (prize: Prize) => void;
}

const prizes: Prize[] = [
  { id: 1, text: 'Giảm 5% dịch vụ', color: '#D4AF37', textColor: '#2C3E50' },      // Gold
  { id: 2, text: 'Voucher 500K', color: '#FF6F61', textColor: '#FFFFFF' },                 // Coral
  { id: 3, text: 'Khám Shop free', color: '#2ECC71', textColor: '#FFFFFF' },       // Emerald
  { id: 4, text: 'Voucher 200K', color: '#3498DB', textColor: '#FFFFFF' },                 // Peter River
  { id: 5, text: 'Meeting 1-1 free', color: '#9B59B6', textColor: '#FFFFFF' },     // Amethyst
  { id: 6, text: 'Voucher 100K', color: '#E67E22', textColor: '#FFFFFF' },                 // Carrot
  { id: 7, text: 'Chúc shop đạt 1000 đơn/ngày', color: '#1ABC9C', textColor: '#FFFFFF' }, // Turquoise
  { id: 8, text: 'Voucher 300K', color: '#F39C12', textColor: '#2C3E50' },                 // Orange
  { id: 9, text: 'Hôm nay bạn tuyệt nhất!', color: '#E91E63', textColor: '#FFFFFF' }, // Pink
  { id: 10, text: 'Chắc chắn là lần sau', color: '#8E44AD', textColor: '#FFFFFF' }, // Wisteria
  { id: 11, text: 'Voucher 50K', color: '#16A085', textColor: '#FFFFFF' }                  // Green Sea
];

const SpinWheel: React.FC<SpinWheelProps> = ({ onPrizeWon }) => {
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [selectedPrize, setSelectedPrize] = useState<Prize | null>(null);
  const [selectedPrizeIndex, setSelectedPrizeIndex] = useState<number | null>(null);
  const glowIntensity = 0.4; // Changed to const since it's never updated
  const [canSpin, setCanSpin] = useState(true);
  const [timeUntilNextSpin, setTimeUntilNextSpin] = useState<string>('');
  const wheelRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const checkSpinAvailability = () => {
    const lastSpinTime = localStorage.getItem('lastSpinTime');

    if (!lastSpinTime) {
      setCanSpin(true);
      setTimeUntilNextSpin('');
      return;
    }

    const lastSpin = parseInt(lastSpinTime, 10);
    const now = Date.now();
    const timeDiff = now - lastSpin;
    const twentyFourHours = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

    if (timeDiff >= twentyFourHours) {
      // Can spin again
      setCanSpin(true);
      setTimeUntilNextSpin('');
    } else {
      // Still in cooldown
      setCanSpin(false);
      const timeLeft = twentyFourHours - timeDiff;
      const hours = Math.floor(timeLeft / (60 * 60 * 1000));
      const minutes = Math.floor((timeLeft % (60 * 60 * 1000)) / (60 * 1000));
      const seconds = Math.floor((timeLeft % (60 * 1000)) / 1000);
      setTimeUntilNextSpin(`${hours}h ${minutes}m ${seconds}s`);
    }
  };

  const drawWheel = React.useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // FIXED: Always use the same canvas size for consistent drawing
    // CSS will handle responsive scaling via transform
    const FIXED_SIZE = 500;
    canvas.width = FIXED_SIZE;
    canvas.height = FIXED_SIZE;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = canvas.width / 2 - 10;
    const sliceAngle = (2 * Math.PI) / prizes.length;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Debug: Log slice positions
    console.log('=== Wheel Drawing Debug ===');
    console.log('Canvas size:', FIXED_SIZE);
    console.log('Number of prizes:', prizes.length);
    console.log('Slice angle (radians):', sliceAngle);
    console.log('Slice angle (degrees):', (sliceAngle * 180 / Math.PI).toFixed(2));

    // Draw each slice
    prizes.forEach((prize, index) => {
      // Start from top (0°) and go clockwise
      // 0° is at 3 o'clock, -90° is at 12 o'clock (top)
      const startAngle = index * sliceAngle - Math.PI / 2;
      const endAngle = startAngle + sliceAngle;
      const centerAngle = startAngle + sliceAngle / 2;

      // Debug log for first few slices
      if (index < 3) {
        console.log(`Slice ${index} (${prize.text}):`, {
          startDeg: (startAngle * 180 / Math.PI).toFixed(2),
          centerDeg: (centerAngle * 180 / Math.PI).toFixed(2),
          endDeg: (endAngle * 180 / Math.PI).toFixed(2)
        });
      }

      // Check if this is the winning slice (after spinning finished)
      const isWinning = !isSpinning && selectedPrizeIndex === index;
      const isNonWinning = !isSpinning && selectedPrizeIndex !== null && selectedPrizeIndex !== index;

      // Draw slice
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = prize.color;
      ctx.fill();

      // Draw border
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 3;
      ctx.stroke();

      // Add shadow/gradient for 3D effect
      const gradient = ctx.createRadialGradient(centerX, centerY, radius * 0.3, centerX, centerY, radius);
      gradient.addColorStop(0, 'rgba(255, 255, 255, 0.3)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0.2)');
      ctx.fillStyle = gradient;
      ctx.fill();

      // Dim non-winning slices
      if (isNonWinning) {
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius, startAngle, endAngle);
        ctx.closePath();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fill();
      }

      // Highlight winning slice with pulsing glow effect
      if (isWinning) {
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius, startAngle, endAngle);
        ctx.closePath();

        // Create pulsing glowing gradient
        const glowGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
        glowGradient.addColorStop(0, `rgba(255, 215, 0, ${glowIntensity})`);
        glowGradient.addColorStop(0.5, `rgba(255, 215, 0, ${glowIntensity * 0.5})`);
        glowGradient.addColorStop(1, 'rgba(255, 215, 0, 0)');
        ctx.fillStyle = glowGradient;
        ctx.fill();

        // Add pulsing golden border
        ctx.strokeStyle = '#FFD700';
        ctx.lineWidth = 5 + glowIntensity * 3;
        ctx.stroke();
      }

      // Draw text
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(startAngle + sliceAngle / 2);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = prize.textColor || '#FFFFFF';
      ctx.font = 'bold 14px Arial';

      // Split text into multiple lines if needed
      const words = prize.text.split(' ');
      const maxWidth = radius * 0.7;
      let line = '';
      const lines: string[] = [];

      words.forEach(word => {
        const testLine = line + word + ' ';
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth && line !== '') {
          lines.push(line.trim());
          line = word + ' ';
        } else {
          line = testLine;
        }
      });
      lines.push(line.trim());

      // Draw each line
      const lineHeight = 16;
      const startY = -(lines.length - 1) * lineHeight / 2;
      lines.forEach((textLine, i) => {
        ctx.fillText(textLine, radius * 0.65, startY + i * lineHeight);
      });

      ctx.restore();
    });

    // Draw center circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, 40, 0, 2 * Math.PI);
    ctx.fillStyle = '#2C3E50';
    ctx.fill();
    ctx.strokeStyle = '#FFD700';
    ctx.lineWidth = 4;
    ctx.stroke();

    // Draw center text
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('QUAY', centerX, centerY);
  }, [isSpinning, selectedPrizeIndex]);

  useEffect(() => {
    const handleResize = () => {
      drawWheel();
    };

    drawWheel();
    checkSpinAvailability();

    // Update timer every second
    const interval = setInterval(() => {
      checkSpinAvailability();
    }, 1000);

    // Handle window resize
    window.addEventListener('resize', handleResize);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', handleResize);
    };
  }, [drawWheel]);

  const spinWheel = () => {
    if (isSpinning || !canSpin) return;

    // Save spin time to localStorage
    localStorage.setItem('lastSpinTime', Date.now().toString());
    setCanSpin(false);

    setIsSpinning(true);
    setSelectedPrize(null);
    setSelectedPrizeIndex(null);

    // Random prize selection
    const selectedIndex = Math.floor(Math.random() * prizes.length);
    const prize = prizes[selectedIndex];

    // Calculate rotation
    const sliceAngle = 360 / prizes.length;

    // KEY INSIGHT:
    // Canvas API draws with radians, starting position for slice 0:
    //   startAngle = 0 * (2π/11) - π/2 (radians) = -90°
    //
    // In CSS rotation (degrees):
    // - Pointer is at top = 0° in CSS coordinates
    // - Canvas slice 0 starts at -90° (12 o'clock)
    // - Each slice center is at: -90° + index * sliceAngle + sliceAngle/2
    //
    // CURRENT ISSUE: After testing, seems like the wheel is stopping at 90° (3 o'clock)
    // instead of 0° (12 o'clock). This means we need to adjust by -90°.
    //
    // If the pointer is ACTUALLY at 12 o'clock (top), then:
    // - slice 0 center at -90° + 16.36° = -73.64°
    // - To align slice N center with top pointer (0°):
    //   rotation needed = 0° - (-90° + N * sliceAngle + sliceAngle/2)
    //                   = 90° - N * sliceAngle - sliceAngle/2

    const currentPosition = ((rotation % 360) + 360) % 360;

    // Position of selected slice center in the initial wheel (before any rotation)
    const sliceCenterInitial = -90 + selectedIndex * sliceAngle + sliceAngle / 2;

    // To make this center point to the top (0°), we need to rotate by its negative
    // But we want TOTAL rotation to end at this angle, not additional
    let targetRotation = -sliceCenterInitial;

    // CORRECTION: There's a 90° offset between canvas drawing and CSS rotation
    // The wheel is landing at 90° (3 o'clock) when we want it at 0° (12 o'clock)
    // So we need to subtract 90° from our target
    targetRotation -= 90;

    // Normalize to 0-360
    targetRotation = ((targetRotation % 360) + 360) % 360;

    // How much more to rotate from where we are now
    let additionalRotation = targetRotation - currentPosition;

    // Always rotate forward
    if (additionalRotation < 0) {
      additionalRotation += 360;
    }

    // Add 5 full spins for effect
    additionalRotation += 360 * 5;

    const newRotation = rotation + additionalRotation;
    setRotation(newRotation);

    
    // Set prize after animation
    setTimeout(() => {
      // Verify the prize by calculating which slice is at the pointer after rotation
      const finalRotation = (newRotation % 360 + 360) % 360;

      // The pointer is at the top (0°). After rotation, we need to find which slice center is closest to 0°
      // Each slice center in the original position is at: -90 + index * sliceAngle + sliceAngle/2
      // After rotation by finalRotation, it's at: (-90 + index * sliceAngle + sliceAngle/2 + finalRotation) % 360

      // We want to find which slice has its center closest to 0° (the pointer position)
      let closestIndex = 0;
      let minDistance = 360;

      for (let i = 0; i < prizes.length; i++) {
        const sliceCenter = (-90 + i * sliceAngle + sliceAngle / 2 + finalRotation + 360) % 360;
        const distance = Math.min(
          Math.abs(sliceCenter - 0),
          Math.abs(sliceCenter - 360)
        );

        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = i;
        }
      }

      console.log('=== Landing Verification ===');
      console.log('Final rotation:', finalRotation.toFixed(2));
      console.log('Landed index:', closestIndex);
      console.log('Expected index:', selectedIndex);
      console.log('Landed prize:', prizes[closestIndex].text);
      console.log('Expected prize:', prize.text);

      // Always use the expected prize (selectedIndex) for consistency
      // The rotation calculation should be exact, so they should match
      setIsSpinning(false);
      setSelectedPrize(prize);
      setSelectedPrizeIndex(selectedIndex);
      if (onPrizeWon) {
        onPrizeWon(prize);
      }
    }, 5000);
  };

  return (
    <div className="spin-wheel-container">
      <div className="spin-wheel-wrapper">
        {/* Decorative elements */}
        <div className="wheel-glow"></div>
        <div className="wheel-particles">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="particle" style={{
              '--particle-delay': `${i * 0.1}s`,
              '--particle-duration': `${2 + Math.random() * 2}s`
            } as React.CSSProperties}></div>
          ))}
        </div>

        {/* Pointer */}
        <div className="wheel-pointer">
          <div className="pointer-triangle"></div>
        </div>

        {/* Wheel */}
        <div
          ref={wheelRef}
          className={`spin-wheel ${isSpinning ? 'spinning' : ''}`}
          style={{ transform: `rotate(${rotation}deg)` }}
        >
          <canvas
            ref={canvasRef}
            className="wheel-canvas"
          />
        </div>

        {/* Center button */}
        <button
          className={`spin-button ${isSpinning || !canSpin ? 'disabled' : ''}`}
          onClick={spinWheel}
          disabled={isSpinning || !canSpin}
          title={!canSpin && !isSpinning ? `Quay lại sau: ${timeUntilNextSpin}` : ''}
        >
          <span className="spin-button-text">
            {isSpinning ? 'ĐANG QUAY...' : !canSpin ? 'ĐÃ QUAY' : 'QUAY NGAY'}
          </span>
          <div className="spin-button-glow"></div>
        </button>

        {/* Cooldown message */}
        {!canSpin && !isSpinning && timeUntilNextSpin && (
          <div className="cooldown-message">
            <p>Bạn đã quay hôm nay!</p>
            <p className="cooldown-timer">Quay lại sau: {timeUntilNextSpin}</p>
          </div>
        )}
      </div>

      {/* Result display */}
      {selectedPrize && !isSpinning && (
        <div className="prize-result">
          <div className="prize-result-content">
            <div className="prize-result-icon">🎉</div>
            <h3 className="prize-result-title">Chúc mừng bạn!</h3>
            <p className="prize-result-text">{selectedPrize.text}</p>
            <button
              className="prize-result-button"
              onClick={() => setSelectedPrize(null)}
            >
              Đóng
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpinWheel;
